import { AgGridAngular } from '@ag-grid-community/angular';
import { AllModules } from '@ag-grid-enterprise/all-modules';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { fuseAnimations } from '@fuse/animations';
import { select, Store } from '@ngrx/store';
import { AnalyticsEntry } from 'app/api/analytics.api.service';
import { UpdatePlanEntryInput } from 'app/api/API.service';
import { PlanEntry } from 'app/api/plan.api.service';
import {
    KeyValueCellRendererComponent
} from 'app/main/shared/key-value-cell-renderer/key-value-cell-renderer.component';
import { AppState } from 'app/reducers';
import { agGridLocale, EurCurrencyCellRenderer } from 'app/utils/ag-grid.locale';
import { AmplifyService } from 'aws-amplify-angular';
import * as _ from 'lodash';
import * as moment from 'moment';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { debounceTime, delay, distinctUntilChanged, filter, map, take, takeUntil, tap } from 'rxjs/operators';

import { selectAccountingByAccount } from '../accounting/accounting.selectors';
import { AnalyticsSelectors } from 'app/main/analytics/selectors';
import { SnackbarOpen } from '../shared/snackbar/snackbar.actions';
import { PlanEntryUpdateRequested, PlanQuery } from './plan.actions';
import { selectPlanAnalytics, selectAna3WithAna1Ana2Count, selectPlanError, selectPlanLoading, selectPlanQuery } from './plan.selectors';
import { CompaniesUISelectors } from '../company/selectors';

export interface PlanRow extends PlanEntry {
    ana1KeyValue?: string;
    ana2KeyValue?: string;
    ana3KeyValue?: string;
    accountLabel?: string;
}

@Component({
    selector: 'plan',
    templateUrl: './plan.component.html',
    styleUrls: ['./plan.component.scss'],
    animations: fuseAnimations,
    encapsulation: ViewEncapsulation.None
})
export class PlanComponent implements OnInit, OnDestroy, AfterViewInit {
    public gridApi;
    public gridColumnApi;

    public columnDefs;
    public defaultColDef;
    public statusBar;
    public localeText;
    public modules;
    public frameworkComponents;
    public editType = '';
    public rowSelection = 'single';
    public getRowNodeId;
    public deltaRowDataMode = true;
    public detailCellRendererParams;
    public masterDetail;
    public suppressCellSelection;
    public enableRangeSelection;

    selectedRow$: BehaviorSubject<PlanRow>;
    keyUp = new Subject<KeyboardEvent>();

    rowData$: Observable<PlanRow[]>;
    loading$: Observable<boolean>;
    @ViewChild('agGridPlan', { static: false }) agGrid: AgGridAngular;

    private ana1List: AnalyticsEntry[];
    private ana2List: AnalyticsEntry[];
    private ana3List: AnalyticsEntry[];

    // Protected
    protected logger: any;

    // Private
    private _unsubscribeAll: Subject<any>;
    private query: PlanQuery;

    constructor(
        protected amplifyService: AmplifyService,
        protected router: Router,
        protected activatedRoute: ActivatedRoute,
        protected store: Store<AppState>,
    ) {
        const instance = this;
        this.logger = this.amplifyService.logger('PlanComponent');
        this._unsubscribeAll = new Subject();
        this.selectedRow$ = new BehaviorSubject(null);
        this.modules = AllModules;
        this.localeText = agGridLocale.fr;
        this.suppressCellSelection = true;
        this.enableRangeSelection = false;

        this.columnDefs = [
            {
                headerName: 'Compta',
                field: 'account',
                minWidth: 170,
                cellRenderer: 'agGroupCellRenderer',
                cellClass: 'grey-100'
            },
            { headerName: 'Libellé', field: 'accountLabel', cellClass: 'grey-100' },
            {
                headerName: 'ANA1',
                field: 'ana1KeyValue',
                minWidth: 100,
                width: 100,
                editable: true,
                cellRenderer: 'keyValueCellRendererComponent',
                cellEditor: 'agRichSelectCellEditor',
                cellEditorParams: params => this.cellEditorAna1(params)
            }, // Select
            {
                headerName: 'ANA2',
                field: 'ana2KeyValue',
                minWidth: 200,
                width: 200,
                editable: true,
                cellRenderer: 'keyValueCellRendererComponent',
                cellEditor: 'agRichSelectCellEditor',
                cellEditorParams: params => this.cellEditorAna2(params)
            }, // Select
            {
                headerName: 'ANA3',
                field: 'ana3KeyValue',
                minWidth: 200,
                width: 200,
                editable: true,
                cellRenderer: 'keyValueCellRendererComponent',
                cellEditor: 'agRichSelectCellEditor',
                cellEditorParams: params => this.cellEditorAna3(params)
            }, // Select
        ];
        this.defaultColDef = {
            enableValue: true,
            sortable: true,
            filter: true,
            resizable: true
        };
        this.statusBar = {
            statusPanels: [
                {
                    statusPanel: 'agTotalRowCountComponent',
                    align: 'left'
                },
                { statusPanel: 'agSelectedRowCountComponent' },
                { statusPanel: 'agFilteredRowCountComponent' }
            ]
        };
        this.frameworkComponents = { keyValueCellRendererComponent: KeyValueCellRendererComponent };
        this.getRowNodeId = data => data.account;

        // MASTER DETAIL
        this.rowSelection = 'single';
        this.masterDetail = true;
        this.detailCellRendererParams = {
            detailGridOptions: {
                localeText: agGridLocale.fr,
                columnDefs: [
                    {
                        headerName: 'Date',
                        field: 'entryDate',
                        valueGetter: params =>
                            params.data
                                ? moment(params.data.entryDate).format(
                                    'YYYY-MM'
                                )
                                : ''
                    },
                    { headerName: 'Journal', field: 'journal' },
                    { headerName: 'Compte', field: 'account' },
                    { headerName: 'Auxiliaire', field: 'auxiliary' },
                    { headerName: 'Libellé', field: 'entryLabel' },
                    {
                        headerName: 'Débit',
                        field: 'debit',
                        type: 'numericColumn',
                        cellRenderer: EurCurrencyCellRenderer
                    },
                    {
                        headerName: 'Crédit',
                        field: 'credit',
                        type: 'numericColumn',
                        cellRenderer: EurCurrencyCellRenderer
                    }
                ],
                onFirstDataRendered(params): void {
                    instance.autoSizeAll(params);
                }
            },
            getDetailRowData: (params: any): void => {
                const account = params.data.account;
                instance.store
                    .pipe(
                        select(selectAccountingByAccount(account)),
                        delay(0),
                        take(1)
                    )
                    .subscribe(entries => {
                        params.successCallback(entries);
                    });
            }
        };
    }

    ngOnInit(): void {

        this.loading$ = this.store.pipe(select(selectPlanLoading));
        this.rowData$ = this.store.pipe(select(selectPlanAnalytics));
        this.store
            .pipe(
                takeUntil(this._unsubscribeAll),
                select(CompaniesUISelectors.selectCurrentCompanyId),
            )
            .subscribe(companyId => {
                window.history.pushState({}, '', `/plan/${companyId}`);
            });

        this.store
            .pipe(
                takeUntil(this._unsubscribeAll),
                select(AnalyticsSelectors.selectAna1),
            )
            .subscribe(entries => {
                this.ana1List = entries;
            });
        this.store
            .pipe(
                takeUntil(this._unsubscribeAll),
                select(AnalyticsSelectors.selectAna2),
            )
            .subscribe(entries => {
                this.ana2List = entries;
            });
        this.store
            .pipe(
                takeUntil(this._unsubscribeAll),
                select(AnalyticsSelectors.selectAna3),
            )
            .subscribe(entries => {
                this.ana3List = entries;
            });
        this.store
            .pipe(
                takeUntil(this._unsubscribeAll),
                select(selectPlanQuery),
                tap(query => {
                    this.query = query;
                    // Set Url
                    this.setUrlFromQuery(query);
                })
            )
            .subscribe();

        this.store
            .pipe(
                takeUntil(this._unsubscribeAll),
                select(selectPlanError),
                filter(message => message !== null),
            )
            .subscribe(message => {
                this.store.dispatch(new SnackbarOpen({
                    message, action: 'OK', config: { panelClass: ['snackbar-error'] }
                }));
            });            

        this.keyUp
            .pipe(
                takeUntil(this._unsubscribeAll),
                map((event: any) => event.target.value),
                debounceTime(250),
                distinctUntilChanged(),
                tap(search => this.gridApi.setQuickFilter(search))
            )
            .subscribe();
    }

    ngAfterViewInit(): void { }

    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    public clearSearch(): void {
        this.gridApi.setQuickFilter('');
    }

    private setUrlFromQuery(query: PlanQuery): void {
        const queryParams: any = {
            account: query.account ? JSON.stringify(query.account) : null,
            filter: query.filter ? JSON.stringify(query.filter) : null
        };
        this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams
        });
    }

    onGridReady(params: { api: any; columnApi: any; }): void {
        this.gridApi = params.api;
        this.gridColumnApi = params.columnApi;
    }

    onFirstDataRendered(params): void {
        this.autoSizeAll(params);
    }

    onCellValueChanged(ev: any): void {
        switch (ev.colDef.field) {
            case 'ana1KeyValue':
                ev.node.setDataValue('ana2KeyValue', undefined);
                ev.node.setDataValue('ana3KeyValue', undefined);
                break;
            case 'ana2KeyValue':
                ev.node.setDataValue('ana3KeyValue', undefined);            
                break;
            case 'ana3KeyValue':
                const { companyId, account } = ev.data;
                const anaKey = ev.data.ana3KeyValue.split('|')[0];
                const input: UpdatePlanEntryInput = {
                    companyId,
                    account,
                    anaKey,
                };
                this.store.dispatch(new PlanEntryUpdateRequested({ input }));            
                break;
            default:
                break;
        }
    }

    onSelectionChanged(): void {
        const selectedRows = this.gridApi.getSelectedRows();
        if (selectedRows.length === 1) {
            this.selectedRow$.next(selectedRows[0]);
        } else {
            this.selectedRow$.next(null);
        }
    }

    onRowDataUpdated(ev: any): void {
    }

    cellEditorAna1(params): any {
        if (!this.ana1List) { return { values: [] }; }
        return {
            values: _.map(this.ana1List, entry => `${entry.key}|${entry.name}`),
            cellRenderer: 'keyValueCellRendererComponent'
        };
    }
    cellEditorAna2(params): any {
        if (!this.ana2List || params.data.ana1KeyValue === '') { return { values: [] }; }
        const parentKey = params.data.ana1KeyValue.split('|')[0];
        const ana2ListFiltered = _.filter(this.ana2List, { parentKey });
        return {
            values: _.map(ana2ListFiltered, entry => `${entry.key}|${entry.name}`),
            cellRenderer: 'keyValueCellRendererComponent'
        };
    }
    cellEditorAna3(params): any {
        if (!this.ana3List || params.data.ana2KeyValue === '') { return { values: [] }; }
        const parentKey = params.data.ana2KeyValue.split('|')[0];
        const ana3ListFiltered = _.filter(this.ana3List, { parentKey });
        return {
            values: _.map(ana3ListFiltered, entry => `${entry.key}|${entry.name}`),
            cellRenderer: 'keyValueCellRendererComponent'
        };
    }

    private autoSizeAll(params: any): void {
        const allColumnIds = params.columnApi
            .getAllColumns()
            .map(column => column.colId);
        params.columnApi.autoSizeColumns(allColumnIds);
    }

}
